import React, { useEffect, useMemo, useState } from 'react';
import { useCart } from '../../contexts/CartProvider';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import { Button, Divider, QRCode, Input, Checkbox } from 'antd';
import { FaPix, FaTag } from 'react-icons/fa6';
import { HiOutlineShieldCheck } from 'react-icons/hi';
import { api } from '../../services/apiClient';
import { toast } from 'react-toastify';
import logoSvg from '../../assets/tribodasorte 2.svg';
import { useSaldo } from '../../contexts/SaldoContext';

const Pix = ({ items, couponCode, setCouponCode, validarCupon }) => {
  const [urlPix, setUrlPix] = useState('');
  const [pixId, setPixId] = useState();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cupomAplicado, setCupomAplicado] = useState(false);
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('Aguardando pagamento');
  const [counter, setCounter] = useState(30);
  const [isSaldoRoute, setIsSaldoRoute] = useState(false);

  const { clearCart, coupon, setTotal } = useCart();
  const navigate = useNavigate();
  const { fetchSaldo } = useSaldo();

  useEffect(() => {
    setIsSaldoRoute(window.location.href.includes('/saldo'));
  }, []);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const totals = useMemo(
    () =>
      items.reduce((acc, item) => {
        if (!acc[item.nome]) acc[item.nome] = 0;
        acc[item.nome] += item.quantidade * item.valor;
        return acc;
      }, {}),
    [items],
  );

  const initialGrandTotal = useMemo(() => Object.values(totals).reduce((acc, value) => acc + value, 0), [totals]);
  const totalQuantidade = useMemo(() => items.reduce((acc, item) => acc + item.quantidade, 0), [items]);

  let grandTotal = initialGrandTotal;
  let desconto = 0;
  if (coupon && coupon.isValid) {
    if (coupon.is_porcentagem === 1) {
      desconto = initialGrandTotal * (coupon.discount / 100);
      grandTotal -= desconto;
    } else {
      desconto = coupon.discount;
      grandTotal -= desconto;
    }
  }

  const handleCupon = async () => {
    if (!couponCode?.trim()) {
      toast.warn('Digite um cupom para aplicar.');
      return;
    }

    const validar = await validarCupon(true);
    if (validar) {
      setCupomAplicado(true);
      toast.success('Cupom aplicado com sucesso!');
      return;
    }

    toast.error('Cupom inválido.');
  };

  const handlePayPix = async () => {
    setLoading(true);
    if (!aceiteTermos) {
      toast.warn('Por favor aceite os termos de uso e a política de privacidade', { position: 'top-right', autoClose: 5000, theme: 'dark' });
      setLoading(false);
      return;
    }

    try {
      if (isSaldoRoute) {
        const responsePayment = await api.post('/carrega_saldo_pix', { total: grandTotal });
        if (responsePayment.data.id && responsePayment.data.init_point) {
          setUrlPix(responsePayment.data.init_point);
          setPixId(responsePayment.data.id);
        }
      } else {
        setTotal(grandTotal);
        const responsePayment = await api.post('/comprar_qr_code', {
          cupom_desconto_id: coupon?.id,
          boloes: items,
          quantity: totalQuantidade,
          total: grandTotal,
        });
        if (responsePayment.data.id && responsePayment.data.init_point) {
          setUrlPix(responsePayment.data.init_point);
          setPixId(responsePayment.data.id);
          setCounter(30);
        }
      }
      fetchSaldo();
    } catch (e) {
      console.log('e', e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPixCode = async () => {
    try {
      await navigator.clipboard.writeText(urlPix);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await api.post('/verificar_pix', { id_pix: pixId });
      if (response.data.ok === true) {
        clearCart();
        window.scrollTo(0, 0);
        fetchSaldo();
        setPaymentStatus('Pago com sucesso');
        navigate(`/compra_concluida?idPagamento=${pixId}`);
        toast.success('Pagamento efetuado com sucesso', { position: 'top-right', autoClose: 5000, theme: 'light' });
      }
    } catch (error) {
      console.log('Error checking payment status:', error.message);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (pixId) checkPaymentStatus();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [pixId]);

  useEffect(() => {
    if (!urlPix) return undefined;
    const timer = setInterval(() => setCounter((prev) => (prev > 0 ? prev - 1 : 30)), 1000);
    return () => clearInterval(timer);
  }, [urlPix]);

  return (
    <div className={styles.checkoutPage}>
      <div className={styles.grid}>
        <section className={styles.leftColumn}>
          {!isSaldoRoute && !urlPix && (
            <div className={styles.cardBox}>
              <div className={styles.sectionTitle}>
                <FaTag />
                <h2>Você tem algum cupom de desconto?</h2>
              </div>
              <p className={styles.sectionDescription}>No Pix, cupons compatíveis são aplicados na hora.</p>
              <div className={styles.couponRow}>
                <Input placeholder='Digite seu cupom' value={couponCode} onChange={(e) => setCouponCode(e.target.value)} disabled={cupomAplicado} />
              </div>
                <Button className={styles.applyButton} onClick={handleCupon} disabled={cupomAplicado}>
                  {cupomAplicado ? 'Aplicado' : 'Aplicar cupom'}
                </Button>
            </div>
          )}

          <div className={styles.cardBox}>
            <div className={styles.sectionTitle}>
              <FaPix />
              <h2>{urlPix ? 'Pagamento Pix gerado' : 'Pagamento via Pix'}</h2>
            </div>
            {!urlPix ? (
              <p className={styles.sectionDescription}>Clique em confirmar para gerar o QR Code de pagamento.</p>
            ) : (
              <div className={styles.pixArea}>
                <QRCode errorLevel='H' value={urlPix} icon={logoSvg} />
                <p>Escaneie o QR Code ou copie o código abaixo.</p>
                <h3>{paymentStatus}</h3>
                <span>Nova verificação em {counter}s</span>
                <div className={styles.copyRow}>
                  <Input value={urlPix} readOnly />
                  <Button className={styles.applyButton} onClick={handleCopyPixCode}>
                    {copied ? 'Copiado!' : 'Copiar'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className={styles.securityBox}>
            <HiOutlineShieldCheck />
            <div>
              <strong>Ambiente protegido</strong>
              <p>A confirmação é automática após o pagamento ser identificado.</p>
            </div>
          </div>
        </section>

        <aside className={styles.rightColumn}>
          <div className={styles.summaryCard}>
            <h2>Resumo do pedido</h2>
            <div className={styles.summaryItems}>
              {Object.keys(totals).map((nome) => (
                <div key={nome} className={styles.summaryRow}>
                  <span>{nome}</span>
                  <strong>{formatCurrency(totals[nome])}</strong>
                </div>
              ))}
              {coupon && coupon.isValid && (
                <div className={`${styles.summaryRow} ${styles.discountRow}`}>
                  <span>Cupom ({couponCode})</span>
                  <strong>-{formatCurrency(desconto)}</strong>
                </div>
              )}
            </div>
            <Divider className={styles.divider} />
            <div className={styles.totalRow}>
              <span>Total</span>
              <h3>{formatCurrency(grandTotal)}</h3>
            </div>
            {!urlPix && (
              <Button loading={loading} disabled={loading} className={styles.confirmButton} onClick={handlePayPix} block>
                Confirmar compra
              </Button>
            )}
            <p className={styles.warningText}>Após finalizar a compra, não será possível estorno automático.</p>
          </div>

          {!urlPix && (
            <div className={styles.termsRow}>
              <Checkbox checked={aceiteTermos} onChange={(e) => setAceiteTermos(e.target.checked)}>
                Li e concordo com os <strong>Termos de Uso</strong> e <strong>Política de Privacidade</strong>
              </Checkbox>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Pix;
